import React from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontal, DollarSignIcon, PackageIcon, TargetIcon, PercentIcon } from 'lucide-react';
import type { DashboardInputs } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
interface InputCardProps {
  inputs: DashboardInputs;
  onChange: (field: keyof DashboardInputs, value: number) => void;
}
const fields: {
  key: keyof DashboardInputs;
  label: string;
  icon: React.ElementType;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
}[] = [{
  key: 'price',
  label: 'Precio de Venta',
  icon: DollarSignIcon,
  min: 10000,
  max: 200000,
  step: 500,
  format: v => formatCurrency(v)
}, {
  key: 'productCost',
  label: 'Costo del Producto',
  icon: PackageIcon,
  min: 1000,
  max: 100000,
  step: 500,
  format: v => formatCurrency(v)
}, {
  key: 'cpaIdeal',
  label: 'CPA Ideal en Meta',
  icon: TargetIcon,
  min: 1000,
  max: 50000,
  step: 500,
  format: v => formatCurrency(v)
}, {
  key: 'deliveryEfficiency',
  label: 'Efectividad de Entrega',
  icon: PercentIcon,
  min: 10,
  max: 100,
  step: 1,
  format: v => `${v}%`
}];
export function InputCard({
  inputs,
  onChange
}: InputCardProps) {
  const handleNumberInput = (key: keyof DashboardInputs, raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      onChange(key, num);
    } else if (cleaned === '') {
      onChange(key, 0);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.5,
    delay: 0.1
  }} className="glass-card p-6 h-full flex flex-col" data-id="element-71">
      <div className="flex items-center gap-3 mb-5" data-id="element-72">
        <div className="p-2 bg-salmon/10 rounded-lg" data-id="element-73">
          <SlidersHorizontal className="w-5 h-5 text-salmon" data-id="element-74" />
        </div>
        <h2 className="text-sm font-bold tracking-widest text-white/70 uppercase" data-id="element-75">
          Panel de Control
        </h2>
      </div>

      <div className="space-y-5 flex-grow flex flex-col justify-center" data-id="element-76">
        {fields.map(field => {
        const value = inputs[field.key];
        const pct = (value - field.min) / (field.max - field.min) * 100;
        return <div key={field.key} className="group" data-id="element-77">
              <div className="flex items-center justify-between mb-2" data-id="element-78">
                <div className="flex items-center gap-2" data-id="element-79">
                  <field.icon className="w-3.5 h-3.5 text-white/40 group-hover:text-salmon transition-colors" data-id="element-80" />
                  <label className="text-xs font-semibold text-white/50 uppercase tracking-wider" data-id="element-81">
                    {field.label}
                  </label>
                </div>
                <div className="relative" data-id="element-82">
                  <input type="text" inputMode="numeric" value={field.key === 'deliveryEfficiency' ? value : value.toLocaleString('en-US')} onChange={e => handleNumberInput(field.key, e.target.value)} className="w-24 text-right bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-sm font-bold text-white focus:outline-none focus:border-salmon/50 transition-colors" data-id="element-83" />
                  {field.key === 'deliveryEfficiency' && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white/30 pointer-events-none" data-id="element-84">
                      %
                    </span>}
                </div>
              </div>

              {/* Range Slider */}
              <div className="relative" data-id="element-85">
                {/* Filled track */}
                <div className="absolute top-1/2 left-0 h-1.5 rounded-full bg-gradient-to-r from-salmon/60 to-salmon pointer-events-none -translate-y-1/2 z-0" style={{
              width: `${pct}%`
            }} data-id="element-86" />
                <input type="range" min={field.min} max={field.max} step={field.step} value={value} onChange={e => onChange(field.key, parseFloat(e.target.value))} className="relative z-10 w-full" data-id="element-87" />
              </div>

              {/* Min/Max labels */}
              <div className="flex justify-between mt-1" data-id="element-88">
                <span className="text-[10px] text-white/20 font-medium" data-id="element-89">
                  {field.key === 'deliveryEfficiency' ? `${field.min}%` : formatCurrency(field.min)}
                </span>
                <span className="text-[10px] text-white/20 font-medium" data-id="element-90">
                  {field.key === 'deliveryEfficiency' ? `${field.max}%` : formatCurrency(field.max)}
                </span>
              </div>
            </div>;
      })}
      </div>

      {/* Fixed Shipping Note */}
      <div className="mt-4 pt-4 border-t border-white/5" data-id="element-91">
        <div className="flex items-center justify-between text-xs" data-id="element-92">
          <span className="text-white/30 font-medium" data-id="element-93">Flete Final (fijo)</span>
          <span className="text-white/50 font-bold" data-id="element-94">
            {formatCurrency(inputs.shipping)}
          </span>
        </div>
      </div>
    </motion.div>;
}